'use client'
import { SubmissionSteps } from "./reportIndividualPage";
import { useEffect, useState } from 'react';
import { useFormContext, ProfessionalTitleOptions } from "./formContext";
import { isValidUrl } from './utils';
import { TitleAndInput } from "./inputFields";
import { StepNavigation } from "./stepNavigation";
import { DropZone } from "./dropZone";
import { fileIcon, getPhotoSizeString, trash, videoIcon } from "../reportingTool/reportingUtils";
import SvgIcon from "../layout/svg-icons/svg-icons";
import { Locations } from "./locations";


interface IProps {
  setCurrentStep: (Step: SubmissionSteps) => void
  markStepAsCompleted: (step: SubmissionSteps) => void
  currentStep: SubmissionSteps
}

export const BiographyPage: React.FC<IProps> = ({
  setCurrentStep,
  markStepAsCompleted,
  currentStep,
}) => {
  const { data, handleFieldChange, removeFile, setData } = useFormContext()

  const [hasUrlError, setUrlErrorText] = useState<undefined | string>(undefined);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showSubmenu, setShowSubmenu] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const menuElement = document.querySelector('[data-menu-container]');
      if (menuElement && !menuElement.contains(event.target as Node)) {
        setSelectedCategory('');
        setShowSubmenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const requiredFields = {
    firstName: data.firstName,
    lastName: data.lastName,
    representativePhoto: data.representativePhoto,
    biographicalURL: data.biographicalURL,
    citizenship: data.citizenship,
  }

  enum CitizenshipOptions {
    US_CITIZEN = 'US Citizen',
    FOREIGN_NATIONAL_UNKNOWN = 'Foreign National - Unknown',
    FOREIGN_NATIONAL = 'Foreign National',
    UNKNOWN_CITIZENSHIP = 'Unknown Citizenship',
  };

  const canProceed =
    [...Object.values(requiredFields)].every(Boolean) && data.representativePhoto.length > 0

  const missingFields = Object.entries(requiredFields).reduce<string[]>(
    (acc, [fieldName, fieldValue]) => {
      if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
        acc.push(fieldName)
      }
      return acc
    },
    [],
  )

  const onClickNextButton = () => {
    setErrorFields(missingFields)
    const isUrlValid = isValidUrl(data.biographicalURL)
    if (isUrlValid) setUrlErrorText(undefined)
    if (missingFields.length > 0 || !isUrlValid) {
      if (!isUrlValid) {
        setUrlErrorText('Please enter a valid URL')
      }
      if (missingFields.length > 0) {
        const firstErrorField = missingFields[0]
        const errorElement = document.getElementById(firstErrorField)
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
      return
    }
    setCurrentStep(currentStep + 1)
    markStepAsCompleted(SubmissionSteps.Biographical)
  }

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    removeFile(data.representativePhoto[index]);
  }

  const setCitizenship = (ans: string) => {
    setData({
      ...data,
      citizenship: ans,
    });
  }

  const handleMouseEnter = (currOpt: string) => {
    if (currOpt === CitizenshipOptions.FOREIGN_NATIONAL) {
      setShowSubmenu(true);
    }
  }

  const handleMouseLeave = () => {
    setShowSubmenu(false);
  }

  const menu = () => {
    return (
      <div className="relative w-full max-w-[300px]" data-menu-container>
        <button
          onClick={() => setSelectedCategory(selectedCategory ? '' : 'citizenship')}
          className={`w-full px-4 py-2 text-left border ${errorFields.includes("citizenship") ? 'border-red-500 border-2' : 'border-gray-300'} rounded-lg bg-white hover:bg-gray-50`}
        >
          {data.citizenship || 'Select Citizenship'}
          <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
            <SvgIcon
              width="20"
              height="20"
              fill="currentColor"
              className="bi bi-chevron-up text-sm font-bold transform rotate-180"
              viewBox="0 0 16 16"
            >
              <path
                fillRule="evenodd"
                d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708z"
              />
            </SvgIcon>
          </div>
        </button>

        {selectedCategory === 'citizenship' && (
          <ul className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20">
            {Object.values(CitizenshipOptions).map((option: CitizenshipOptions) => (
              <li
                key={option}
                className="p-2 hover:bg-gray-100 cursor-pointer relative group flex flex-row items-center justify-between"
                data-last-option={option === CitizenshipOptions.UNKNOWN_CITIZENSHIP ? true : undefined}
                onClick={option === CitizenshipOptions.FOREIGN_NATIONAL ? undefined : () => {
                  setCitizenship(option);
                  setSelectedCategory('');
                  setShowSubmenu(false);
                }}
                onMouseEnter={() => handleMouseEnter(option)}
                onMouseLeave={handleMouseLeave}
              >
                {option}
                {option === CitizenshipOptions.FOREIGN_NATIONAL ? <SvgIcon
                  width="20"
                  height="20"
                  fill="currentColor"
                  className="bi bi-chevron-up text-sm font-bold transform rotate-90"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708z"
                  />
                </SvgIcon> : ''}
                {option === CitizenshipOptions.FOREIGN_NATIONAL && showSubmenu && (
                  <ul
                    data-submenu
                    className="absolute z-50 left-[calc(75%-5px)] top-0 bg-white border border-gray-300 rounded-lg shadow-lg ml-2 max-h-[200px] overflow-y-auto w-[250px]"
                    onMouseLeave={handleMouseLeave}
                  >
                    {Locations.map((location) => (
                      <li
                        key={location}
                        className="p-2 hover:bg-gray-100 cursor-pointer whitespace-nowrap"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCitizenship(location);
                          setSelectedCategory('');
                          setShowSubmenu(false);
                        }}
                      >
                        {location}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="text-[20px] mb-[10px] font-bold leading-[30px] md:text-[30px] md:leading-[45px]">
        Basic Details
      </div>
      <div className="text-[14px] font-medium leading-[21px] mb-[20px] md:text-[16px] md:leading-[24px]">
        Enter the individual&apos;s biographical information here
      </div>
      <div className='border-[1px] rounded-[8px] bg-gray-100 border-gray-600 p-4'>
        <div className="flex flex-wrap md:gap-4">
          <TitleAndInput title='First Name' required={true} errorFields={errorFields} type={'text'} inputValue={data.firstName} id={"firstName"} name={'firstName'} placeholder={'Enter first name'} handleFieldChange={handleFieldChange} />
          <TitleAndInput title="Middle Name" required={false} type="text" inputValue={data.middleName} id={"middleName"} name="middleName" placeholder="Enter middle name" handleFieldChange={handleFieldChange} />
        </div>
        <div className="flex flex-wrap md:gap-4">
          <TitleAndInput title="Last Name" required={true} errorFields={errorFields} type="text" id={'lastName'} inputValue={data.lastName} name="lastName" placeholder="Enter last name" handleFieldChange={handleFieldChange} additionalClass="min-w-[50%]" />
        </div>
        <div className='mb-2'>
          <div className='text-[16px] mb-1 font-medium leading-[24px]'>Photograph of Individual<span className={`text-red-700`}> *</span></div>
          <div className="text-[14px] font-normal leading-[21px] mb-3">
            Make sure this is a clear image of the person. Their face must be fully showing, and they must be the only person in the photograph.
            LinkedIn pictures, public student directory pictures, and yearbook photos are all good choices.{'\u00A0'}<span className='text-[14px] font-medium leading-[21px]'>Acceptable formats: png, jpg, jpeg, gif, pdf.</span>
          </div>
          <DropZone hasError={errorFields.includes("representativePhoto")} id={'representativePhoto'} target="representativePhoto" />
          <div className={'flex flex-col mt-2'}>
            {data.representativePhoto.map((photo, index) => {
              return (
                <div key={index} className={'flex flex-row mt-1 items-center p-2 justify-between w-full'}>
                  <div className="text-[14px] flex items-center break-all" title={photo.name}>
                    <div>{photo.type.startsWith('video') ? videoIcon : fileIcon}</div>
                    <div className={'flex flex-col ml-2'}>
                      <div>{photo.name}</div>
                      <div>{getPhotoSizeString(photo)}</div>
                    </div>
                  </div>
                  <button
                    className={`w-[24px] h-[24px] flex items-center justify-center !text-white ml-[20px] text-white`}
                    onClick={(e) => handleRemove(e, index)}
                  >
                    {trash}
                  </button>
                </div>
              )
            })}
          </div>
          {errorFields.includes("representativePhoto") ? <div className={'text-red-500 italic text-sm mt-2'}>Please upload a file</div> : null}
        </div>
        <div className="flex flex-wrap md:gap-4">
          <TitleAndInput title="University or Company" required={false} type="text" inputValue={data.university} name="university" placeholder="Enter university" handleFieldChange={handleFieldChange} />
          <div className='flex flex-col mb-1 flex-1'>
            <div className="text-[16px] font-medium leading-[24px] mb-1">
              Professional Title / Role
              <span className={'text-[12px] ml-1'}>(Optional)</span>
            </div>
            <select className={`pl-3 rounded-[8px] mb-2 h-[44px]`} value={data.professionalTitle} name="professionalTitle" onChange={handleFieldChange}>
              <option value=''>-- Select an option --</option>
              <option value={ProfessionalTitleOptions.Student}>Student</option>
              <option value={ProfessionalTitleOptions.Professor}>Professor</option>
              <option value={ProfessionalTitleOptions.Medical}>Medical Professional</option>
              <option value={ProfessionalTitleOptions.Other}>Other</option>
            </select>
          </div>
        </div>
        <div className="flex flex-col mb-2">
          <div className='text-[16px] mb-2 font-medium leading-[24px]'>Citizenship<span className={`text-red-700`}> *</span></div>
          {menu()}
          {errorFields.includes("citizenship") ? <div className={'text-red-500 italic text-sm mt-1'}>Please fill in required field</div> : null}
        </div>
        <TitleAndInput errorText={hasUrlError} errorFields={errorFields} title="URL that shows the person's identity" required={true} type="url" inputValue={data.biographicalURL} id={"biographicalURL"} name="biographicalURL" placeholder="Enter URL" handleFieldChange={handleFieldChange} additionalClass={"min-w-[50%]"} />
        <div className="text-[14px] font-normal leading-[21px]">
          As part of our comprehensive investigation process, we require a URL that can confirm the biographical details of the person. University
          websites, news articles, or LinkedIn profiles are ideal for this verification.
        </div>
        <div className='opacity-0 absolute top-0 left-0 h-0 w-0 -z-10'>
          <div>UserName and Password</div>
          <input type="text" id="username" value={data.username} onChange={handleFieldChange} placeholder="Enter Your Username" name="username" />
          <input type="text" id="password" value={data.password} onChange={handleFieldChange} placeholder="Enter Your Password" name="password" />
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-2">
        <StepNavigation
          onClickNext={() => onClickNextButton()}
          hasNext={true}
          hasPrev={false}
          isNextDisabled={!canProceed}
        />
      </div>
    </div>
  )
}
